import React, { useState, useEffect } from "react";
import { 
  FiMessageSquare, FiCheck, FiClock, FiAlertCircle, 
  FiTrash2, FiChevronLeft, FiStar, FiMail, FiUser 
} from "react-icons/fi";
import { useAuth } from "../context/AuthContext";
import { api } from "../services/apiService";
import { toast } from "react-toastify";

const FeedbackPage = () => {
  const { admin } = useAuth();
  const [feedbacks, setFeedbacks] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 1
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [commentText, setCommentText] = useState("");

  const fetchFeedbacks = async () => {
    try {
      setLoading(true);
      setError(null);
      
      if (!admin?.token) {
        throw new Error("Authentication token not available");
      }

      const response = await api.get(`/api/feedback`, {
        params: {
          page: pagination.page,
          limit: pagination.limit,
          status: statusFilter === "all" ? undefined : statusFilter,
          search: searchTerm || undefined
        }
      });
      
      setFeedbacks(response.data.data.docs);
      setPagination({
        page: response.data.data.page,
        limit: response.data.data.limit,
        total: response.data.data.totalDocs,
        totalPages: response.data.data.totalPages
      });
    } catch (err) {
      console.error("Feedback fetch error:", err);
      setError(err.response?.data?.message || "Failed to fetch feedbacks");
      if (err.response?.status === 401) {
        toast.error("Session expired. Please log in again.");
      } else {
        toast.error(err.response?.data?.message || "Failed to fetch feedbacks");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (admin) {
      fetchFeedbacks();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin, pagination.page, statusFilter, searchTerm]);

  const updateStatus = async (id, status) => {
    try {
      const response = await api.put(
        `/api/feedback/${id}/status`,
        { status }
      );
      
      setFeedbacks(feedbacks.map(fb => 
        fb._id === id ? response.data.data : fb
      ));
      
      if (selectedFeedback && selectedFeedback._id === id) {
        setSelectedFeedback(response.data.data);
      }
      
      toast.success(`Feedback marked as ${status}`);
    } catch (err) {
      console.error("Status update error:", err);
      toast.error(err.response?.data?.message || "Failed to update status");
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation(); // Prevent row click event from firing
    if (!window.confirm("Are you sure you want to delete this feedback?")) return;

    setIsDeleting(true);
    try {
      await api.delete(`/api/feedback/${id}`);
      
      setFeedbacks(feedbacks.filter(fb => fb._id !== id));
      
      if (selectedFeedback && selectedFeedback._id === id) {
        setSelectedFeedback(null);
      }
      
      toast.success("Feedback deleted successfully");
    } catch (error) {
      console.error("Delete error:", error);
      toast.error(error.response?.data?.message || "Failed to delete feedback");
    } finally {
      setIsDeleting(false);
    }
  };

  const addComment = async () => {
    if (!commentText.trim()) {
      toast.error("Comment text cannot be empty");
      return;
    }

    try {
      const response = await api.post(
        `/api/feedback/${selectedFeedback._id}/comments`,
        { text: commentText }
      );
      
      setSelectedFeedback(response.data.data);
      setCommentText("");
      toast.success("Comment added successfully");
    } catch (error) {
      console.error("Comment error:", error);
      toast.error(error.response?.data?.message || "Failed to add comment");
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "pending": return <FiClock className="text-yellow-500" />;
      case "reviewed": return <FiCheck className="text-blue-500" />;
      case "resolved": return <FiCheck className="text-green-500" />;
      default: return <FiAlertCircle className="text-gray-500" />;
    }
  };

  const renderStars = (rating) => {
    return Array(5).fill(0).map((_, i) => (
      <FiStar 
        key={i} 
        className={`${i < rating ? "text-yellow-500 fill-yellow-500" : "text-gray-300"}`} 
      />
    ));
  };

  if (!admin) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center p-6 max-w-md">
          <FiAlertCircle className="mx-auto h-12 w-12 text-red-500" />
          <h3 className="mt-2 text-lg font-medium text-gray-900">
            Unauthorized Access
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            You don't have permission to view this page.
          </p>
        </div>
      </div>
    );
  }

  if (loading && feedbacks.length === 0) {
    return (
      <div className="flex justify-center items-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#166534]"></div>
      </div>
    );
  }

  if (error && feedbacks.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center p-6 max-w-md">
          <FiAlertCircle className="mx-auto h-12 w-12 text-red-500" />
          <h3 className="mt-2 text-lg font-medium text-gray-900">
            Error Loading Feedback
          </h3>
          <p className="mt-1 text-sm text-gray-500">{error}</p>
          <button
            onClick={fetchFeedbacks}
            className="mt-4 px-4 py-2 bg-[#166534] text-white rounded-md hover:bg-[#0e3d1f]"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <div className="p-3 rounded-lg bg-[#166534]">
            <FiMessageSquare className="h-6 w-6 text-white" />
          </div>
          <h1 className="ml-3 text-2xl font-medium text-gray-900">User Feedback</h1>
        </div>
        
        {selectedFeedback && (
          <button
            onClick={() => setSelectedFeedback(null)}
            className="flex items-center text-sm text-[#166534] hover:text-[#0e3d1f]"
          >
            <FiChevronLeft className="mr-1" />
            Back to list
          </button>
        )}
      </div>

      {!selectedFeedback ? (
        <>
          <div className="mb-6 flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search feedback..."
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#166534]"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="w-full sm:w-auto">
              <select
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#166534]"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="reviewed">Reviewed</option>
                <option value="resolved">Resolved</option>
              </select>
            </div>
          </div>

          <div className="bg-white shadow overflow-hidden sm:rounded-lg">
            {feedbacks.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                No feedback found matching your criteria.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Name/Email
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Subject
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Rating
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {feedbacks.map((feedback) => (
                      <tr 
                        key={feedback._id} 
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => setSelectedFeedback(feedback)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                              <FiUser className="text-gray-500" />
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900">{feedback.name}</div>
                              <div className="text-sm text-gray-500">{feedback.email}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900 max-w-xs truncate">{feedback.subject}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex">
                            {renderStars(feedback.rating)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {getStatusIcon(feedback.status)}
                            <span className="ml-2 text-sm text-gray-500 capitalize">{feedback.status}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-500">
                            {new Date(feedback.createdAt).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex space-x-2 justify-end">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                updateStatus(feedback._id, "reviewed");
                              }}
                              className={`px-2 py-1 rounded text-xs font-medium ${
                                feedback.status === "reviewed"
                                  ? "bg-blue-100 text-blue-800"
                                  : "bg-gray-100 text-gray-800 hover:bg-blue-50 hover:text-blue-600"
                              }`}
                            >
                              Reviewed
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                updateStatus(feedback._id, "resolved");
                              }}
                              className={`px-2 py-1 rounded text-xs font-medium ${
                                feedback.status === "resolved"
                                  ? "bg-green-100 text-green-800"
                                  : "bg-gray-100 text-gray-800 hover:bg-green-50 hover:text-green-600"
                              }`}
                            >
                              Resolved
                            </button>
                            <button
                              onClick={(e) => handleDelete(feedback._id, e)}
                              disabled={isDeleting}
                              className="text-red-500 hover:text-red-700 p-1"
                            >
                              <FiTrash2 />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {feedbacks.length > 0 && (
            <div className="mt-4 flex justify-between items-center">
              <div className="text-sm text-gray-500">
                Showing {feedbacks.length} of {pagination.total} feedbacks
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: Math.max(prev.page - 1, 1) }))}
                  disabled={pagination.page === 1}
                  className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50"
                >
                  Previous
                </button>
                <button
                  onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                  disabled={pagination.page >= pagination.totalPages}
                  className="px-3 py-1 border border-gray-300 rounded-md disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-lg">
          <div className="px-6 py-5 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium text-gray-900">{selectedFeedback.subject}</h3>
              <div className="flex items-center">
                {getStatusIcon(selectedFeedback.status)}
                <span className="ml-2 text-sm text-gray-500 capitalize">{selectedFeedback.status}</span>
              </div>
            </div>
            <div className="mt-2 flex items-center text-sm text-gray-500">
              <FiMail className="mr-1" />
              <span>{selectedFeedback.email}</span>
            </div>
          </div>

          <div className="px-6 py-5 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <h4 className="text-sm font-medium text-gray-500">Name</h4>
                <p className="mt-1 text-sm text-gray-900">{selectedFeedback.name}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500">Rating</h4>
                <div className="mt-1 flex">
                  {renderStars(selectedFeedback.rating)}
                  <span className="ml-2 text-sm text-gray-900">({selectedFeedback.rating}/5)</span>
                </div>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500">Submitted</h4>
                <p className="mt-1 text-sm text-gray-900">
                  {new Date(selectedFeedback.createdAt).toLocaleString()}
                </p>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-500">Message</h4>
              <p className="mt-1 text-sm text-gray-900 whitespace-pre-line">
                {selectedFeedback.message}
              </p>
            </div>

            <div className="border-t border-gray-200 pt-4">
              <div className="flex space-x-3">
                <button
                  onClick={() => updateStatus(selectedFeedback._id, "reviewed")}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium ${
                    selectedFeedback.status === "reviewed"
                      ? "bg-blue-100 text-blue-800"
                      : "bg-gray-100 text-gray-800 hover:bg-blue-50 hover:text-blue-600"
                  }`}
                >
                  Mark as Reviewed
                </button>
                <button
                  onClick={() => updateStatus(selectedFeedback._id, "resolved")}
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium ${
                    selectedFeedback.status === "resolved"
                      ? "bg-green-100 text-green-800"
                      : "bg-gray-100 text-gray-800 hover:bg-green-50 hover:text-green-600"
                  }`}
                >
                  Mark as Resolved
                </button>
                <button
                  onClick={(e) => handleDelete(selectedFeedback._id, e)}
                  disabled={isDeleting}
                  className="flex-1 py-2 px-4 rounded-md text-sm font-medium bg-red-100 text-red-800 hover:bg-red-50 hover:text-red-600"
                >
                  Delete Feedback
                </button>
              </div>
            </div>

            <div className="border-t border-gray-200 pt-4">
              <h4 className="text-sm font-medium text-gray-500 mb-3">Comments</h4>
              
              {selectedFeedback.comments?.length > 0 ? (
                <div className="space-y-4">
                  {selectedFeedback.comments.map((comment, index) => (
                    <div key={index} className="bg-gray-50 p-3 rounded-lg">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {comment.postedBy?.name || "Admin"}
                          </p>
                          <p className="text-xs text-gray-500">
                            {new Date(comment.createdAt).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <p className="mt-1 text-sm text-gray-700 whitespace-pre-line">
                        {comment.text}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No comments yet</p>
              )}

              <div className="mt-4">
                <textarea
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#166534]"
                  placeholder="Add a comment..."
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                />
                <button
                  onClick={addComment}
                  className="mt-2 px-4 py-2 bg-[#166534] text-white rounded-md hover:bg-[#0e3d1f]"
                >
                  Add Comment
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FeedbackPage;